import { CurrencyInput, Input, SegmentedControl, formatCurrency } from '@hue/design-system';
import { useId, useState, type ReactNode } from 'react';
import { formatPercent, parsePercent, percentOf, type AmountMode } from './sale-cart.js';

/// Um campo de valor em reais ou em porcentagem da base, com a troca que converte o que já foi
/// digitado (ADR 0059).
export function AmountField({
  label,
  mode,
  onMode,
  cents,
  onCents,
  basisPoints,
  onBasisPoints,
  baseCents,
  percentAllowed,
  hint,
  currency = 'BRL',
  inline = false,
  visibleLabel,
  error,
}: {
  label: string;
  mode: AmountMode;
  onMode: (mode: AmountMode) => void;
  cents: number;
  onCents: (cents: number | null) => void;
  basisPoints: number;
  onBasisPoints: (basisPoints: number) => void;
  baseCents: number;
  percentAllowed: boolean;
  hint?: string | undefined;
  currency?: string | undefined;
  /// Rótulo à esquerda e campo à direita, numa linha só, como nas linhas do resumo da venda.
  inline?: boolean | undefined;
  /// O rótulo à vista na linha, com destaque; o nome acessível continua sendo `label`.
  visibleLabel?: ReactNode | undefined;
  /// O valor em reais não serve; o campo mostra o que foi digitado e avisa, sem corrigir sozinho.
  error?: string | undefined;
}) {
  const errorId = useId();
  /// O que a pessoa está digitando; fora da digitação, o campo mostra a porcentagem guardada.
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? formatPercent(basisPoints).replace(' %', '');
  const commit = () => {
    if (draft !== null) onBasisPoints(parsePercent(draft));
    setDraft(null);
  };
  return (
    <div className="grid gap-1">
      {/* O rótulo fica acima da linha toda; assim o seletor e o campo começam na mesma altura. */}
      {inline ? null : (
        <span aria-hidden className="mb-1 block text-xs font-semibold text-content/70">
          {visibleLabel ?? label}
        </span>
      )}
      <div
        className={
          inline ? 'flex flex-wrap items-center justify-end gap-2' : 'flex items-start gap-2'
        }
      >
        {inline ? (
          <span aria-hidden className="mr-auto whitespace-nowrap text-content">
            {visibleLabel ?? label}
          </span>
        ) : null}
        {/* Sem espaço para o rótulo, o seletor e o campo descem juntos para a linha de baixo. */}
        <div className={inline ? 'flex items-center gap-2' : 'contents'}>
          {percentAllowed ? (
            // Com a mesma altura do campo, o seletor fica alinhado a ele em qualquer densidade.
            <SegmentedControl
              compact
              label="Reais ou porcentagem"
              value={mode}
              onChange={onMode}
              options={[
                { value: 'BRL' as const, label: 'R$', accessibleLabel: 'Em reais' },
                { value: 'PCT' as const, label: '%', accessibleLabel: 'Em porcentagem' },
              ]}
            />
          ) : null}
          <div className={inline ? 'w-32 min-w-0' : 'min-w-0 flex-1'}>
            {mode === 'PCT' && percentAllowed ? (
              <Input
                label={`${label} (%)`}
                inputMode="decimal"
                value={text}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commit}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') commit();
                }}
                hint={hint}
                hideLabel
              />
            ) : (
              <CurrencyInput
                label={label}
                currency={currency}
                value={cents}
                onValueChange={onCents}
                hint={hint}
                hideLabel
                // Na linha, o aviso desce para a largura toda em vez de espremer embaixo do campo.
                {...(inline
                  ? error
                    ? { 'aria-invalid': true, 'aria-describedby': errorId }
                    : {}
                  : { error })}
              />
            )}
          </div>
        </div>
      </div>
      {inline && error && !(mode === 'PCT' && percentAllowed) ? (
        <p id={errorId} role="alert" className="text-right text-xs text-danger">
          {error}
        </p>
      ) : null}
      {mode === 'PCT' && percentAllowed ? (
        <p className={`text-xs tabular-nums text-content-muted${inline ? ' text-right' : ''}`}>
          {formatPercent(basisPoints)} de {formatCurrency(baseCents, currency)} ={' '}
          <strong>{formatCurrency(percentOf(baseCents, basisPoints), currency)}</strong>
        </p>
      ) : null}
    </div>
  );
}
